import {
  DragCancelEvent,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
} from "@dnd-kit/core";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import {
  combineDragPassengers,
  createSuccessResult,
  DragTypeEnum,
  DropTypeEnum,
  errors,
  IRideUpdateResultDto,
  isSoftValidationSingleErrorResponse,
  Log,
  NotDraggingDataConst,
  ridesDraggingDataAtom,
  ridesDraggingStationsTempIdsAtom,
  RideSoftValidationErrorResponse,
  SimpleResult,
  TDragCardBoardRide,
  TDragDropSortable,
  TDragGridPassenger,
  TDragGridRide,
  TDragGridStation,
  TDragKanbanPassenger,
  TDragKanbanRide,
  TDragRideData,
  TDropRideData,
  TStationComposedId,
  warnAndToastSimpleResultWithFallback,
} from "@ryde/shared";

import { useGlobalEffectActions } from "../../use-global-effect-actions";
import { useDragPassengersCreateRide } from "./use-drag-passengers-create-ride";
import { useDragPassengersMove } from "./use-drag-passengers-move";
import { useDragRideStationsReorder } from "./use-drag-ride-stations-reorder";
import { useDragRideToRide } from "./use-drag-ride-to-ride";
import { useRideIsDroppable } from "./use-ride-is-droppable";

export const useRidesDragHandling = () => {
  const { t } = useTranslation();
  const setRideDraggingData = useSetRecoilState(ridesDraggingDataAtom);
  const { dragMovePassengers } = useDragPassengersMove();
  const { dragReorderStationsRide } = useDragRideStationsReorder();
  const { dragRideToRide } = useDragRideToRide();
  const { isRideDroppable } = useRideIsDroppable();
  const { startDragAppEvent } = useGlobalEffectActions();
  const { dragPassengersCreateRide } = useDragPassengersCreateRide();
  const setTempDragStationsIds = useSetRecoilState(
    ridesDraggingStationsTempIdsAtom,
  );

  const dragCleanup = useEvent(() => {
    setTempDragStationsIds(undefined);
    setRideDraggingData(NotDraggingDataConst);
  });

  const logDropOperation = (
    dragSource: TDragRideData,
    dropTarget: TDropRideData,
  ): void => {
    Log.debug(
      "Start processing drop operation from source:",
      dragSource,
      "drop target:",
      dropTarget,
    );
  };

  const handleStationDrag = (
    dragSource: TDragGridStation,
    dropTarget: TDropRideData,
  ): Promise<SimpleResult<unknown>> => {
    const source = dragSource as TDragDropSortable<TStationComposedId>;
    const destination =
      dropTarget as unknown as TDragDropSortable<TStationComposedId>;
    return dragReorderStationsRide(source, destination);
  };

  const handlePassengerMove = async (
    dragSource: TDragKanbanPassenger | TDragGridPassenger,
    dropTarget: TDropRideData,
  ): Promise<
    SimpleResult<
      IRideUpdateResultDto | RideSoftValidationErrorResponse | undefined
    >
  > => {
    const passengerRowProps = dragSource.payload;

    if (!dropTarget.rideDrop?.id) {
      return new SimpleResult({ error: t(errors.ride.invalidRideId) });
    }

    return dragMovePassengers(passengerRowProps, dropTarget.rideDrop);
  };

  const handleRideToRide = (
    dragSource: TDragKanbanRide | TDragGridRide | TDragCardBoardRide,
    dropTarget: TDropRideData,
  ): Promise<
    SimpleResult<
      IRideUpdateResultDto | RideSoftValidationErrorResponse | undefined
    >
  > => {
    return dragRideToRide({ dragSource, dropTarget });
  };

  const handlePassengerCreateRide = async (
    dragSource: TDragKanbanRide | TDragGridRide,
    dropTarget: TDropRideData,
  ): Promise<
    SimpleResult<
      IRideUpdateResultDto | RideSoftValidationErrorResponse | undefined
    >
  > => {
    const { rideDto } = dragSource.payload;

    if (!rideDto?.id) {
      return new SimpleResult({ error: t(errors.ride.invalidRideId) });
    }

    const passengerInfos = rideDto?.metadata?.passengersInfo;
    return dragPassengersCreateRide(passengerInfos, rideDto);
  };

  const handlePassengerDrop = async (
    dragSource: TDragKanbanPassenger | TDragGridPassenger,
  ): Promise<
    SimpleResult<
      IRideUpdateResultDto | RideSoftValidationErrorResponse | undefined
    >
  > => {
    const passengerRowProps = dragSource.payload;
    const passengerInfos = combineDragPassengers(passengerRowProps);
    const rideFrom = passengerRowProps.rideDto;

    if (!passengerInfos?.length) {
      return new SimpleResult({
        error: t(errors.ride.invalidDragDropRideDataNoPassengersToDrop),
      });
    }

    if (!rideFrom?.id) {
      return new SimpleResult({ error: t(t(errors.ride.invalidRideId)) });
    }

    return dragPassengersCreateRide(passengerInfos, rideFrom);
  };

  const processDropAction = useEvent(
    async (
      dragSource: TDragRideData,
      dropTarget: TDropRideData,
    ): Promise<SimpleResult<unknown>> => {
      logDropOperation(dragSource, dropTarget);
      const typeDrag = dragSource.type;
      const typeDrop = dropTarget.typeDrop;

      if (typeDrag === DragTypeEnum.NOT_DRAGGING) {
        return new SimpleResult({
          error: t(errors.ride.invalidDragDropRideData),
        });
      }

      const isPassengerDrag =
        typeDrag === DragTypeEnum.KANBAN_PASSENGER ||
        typeDrag === DragTypeEnum.GRID_PASSENGER;
      const sourceRideId = isPassengerDrag
        ? dragSource.payload.rideDto?.id
        : undefined;

      if (isPassengerDrag && dropTarget.rideDrop?.id === sourceRideId) {
        return createSuccessResult();
      }

      const isDroppableCheckResult = isRideDroppable({
        dragSource,
        dropTarget,
      });

      if (!isDroppableCheckResult.success) {
        return isDroppableCheckResult;
      }

      if (typeDrag === DragTypeEnum.STATION) {
        return handleStationDrag(dragSource, dropTarget);
      }

      if (
        (typeDrag === DragTypeEnum.KANBAN_PASSENGER &&
          typeDrop === DropTypeEnum.KANBAN_RIDE_DROP) ||
        (typeDrag === DragTypeEnum.KANBAN_PASSENGER &&
          typeDrop === DropTypeEnum.CARD_BOARD_RIDE_DROP) ||
        (typeDrag === DragTypeEnum.GRID_PASSENGER &&
          typeDrop === DropTypeEnum.GRID_RIDE_DROP)
      ) {
        return handlePassengerMove(dragSource, dropTarget);
      }

      if (
        (typeDrag === DragTypeEnum.KANBAN_RIDE &&
          typeDrop === DropTypeEnum.KANBAN_RIDE_DROP) ||
        (typeDrag === DragTypeEnum.GRID_RIDE &&
          typeDrop === DropTypeEnum.GRID_RIDE_DROP) ||
        (typeDrag === DragTypeEnum.CARD_BOARD_RIDE &&
          typeDrop === DropTypeEnum.CARD_BOARD_RIDE_DROP)
      ) {
        return handleRideToRide(dragSource, dropTarget);
      }

      if (
        (typeDrag === DragTypeEnum.KANBAN_RIDE &&
          typeDrop === DropTypeEnum.NAV_PLUS_BUTTON) ||
        (typeDrag === DragTypeEnum.GRID_RIDE &&
          typeDrop === DropTypeEnum.NAV_PLUS_BUTTON)
      ) {
        return handlePassengerCreateRide(dragSource, dropTarget);
      }

      if (
        (typeDrag === DragTypeEnum.KANBAN_PASSENGER ||
          typeDrag === DragTypeEnum.GRID_PASSENGER) &&
        typeDrop === DropTypeEnum.NAV_PLUS_BUTTON
      ) {
        return handlePassengerDrop(dragSource);
      }

      return new SimpleResult({
        error: t(errors.ride.invalidDragDropRideData),
      });
    },
  );

  const handleDragStart = useEvent((event: DragStartEvent): void => {
    const { active } = event;
    const { data } = active;
    const dragData = (data.current as TDragRideData) || NotDraggingDataConst;
    const dragTypeId = dragData.type;
    Log.debug(`Start dragging of type:${dragTypeId}`, data.current, dragData);

    startDragAppEvent();

    setRideDraggingData(dragData);
  });

  const handleDragEndActionHandler = useEvent(
    async (event: DragEndEvent): Promise<SimpleResult<unknown>> => {
      const { active, over } = event;
      const { data: dataSource } = active;
      const { data: dataTarget } = over || {};
      Log.debug("Handle drag end, source data:", { ...dataSource.current });
      Log.debug("Handle drag end,target data:", { ...dataTarget?.current });

      if (!dataSource.current) {
        dragCleanup();
        return new SimpleResult({
          error: t(errors.ride.invalidDragSourceData),
        });
      }

      if (!dataTarget?.current) {
        dragCleanup();
        return new SimpleResult({
          error: t(errors.ride.invalidDragTargetData),
        });
      }

      const sourceData = { ...dataSource.current } as TDragRideData;
      const targetData = { ...dataTarget.current } as TDropRideData;

      try {
        const dragResultFlag = await processDropAction(sourceData, targetData);
        setRideDraggingData(NotDraggingDataConst);
        return dragResultFlag;
      } catch (e) {
        return new SimpleResult({
          error: t(errors.ride.invalidDragTargetData),
          errorAdditionalDetails: e,
        });
      } finally {
        dragCleanup();
      }
    },
  );

  const handleDragEnd = useEvent(async (event: DragEndEvent): Promise<void> => {
    const dragEndResult = await handleDragEndActionHandler(event);

    if (dragEndResult.success) {
      Log.debug("Drag operation finished successfully", event);
    } else if (!isSoftValidationSingleErrorResponse(dragEndResult.data)) {
      warnAndToastSimpleResultWithFallback(
        dragEndResult,
        t,
        errors.ride.failedUpdateRide,
      );
    }
  });

  const handleDragOver = useEvent((event: DragOverEvent): void => {
    const { active, over } = event;
    const { data } = active;

    const dragTypeId = data?.current?.type;
    Log.debug(
      `Drag over:${dragTypeId}, payload:`,
      data?.current?.payload,
      "over item data:",
      over?.data?.current,
    );
  });

  const handleDragCancel = useEvent((event: DragCancelEvent): void => {
    dragCleanup();
    Log.debug(`Drag canceled`, event?.active?.data?.current?.payload); // TODO: add translation solution/key ? add as toast?
  });

  return { handleDragStart, handleDragOver, handleDragEnd, handleDragCancel };
};
